import type { EntityOptions } from 'models';
import { useCallback, useEffect, useState } from 'react';
import { Card, Col, Empty, type EmptyProperties, Icon, Row } from 'ui';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- TODO: break circular dependency
import { CommunitiesSlider } from 'features/communities/components';
import { EventsSlider } from 'features/events';
import { GivingsSlider } from 'features/givings';
import { ProgramsSlider } from 'features/programs';
import { VideosSlider } from 'features/videos';
import type { ChannelScope } from '../models';

type P = {
  emptyDescription: EmptyProperties['description'];
  getPathToEntityDetail: (id: string, options: EntityOptions) => string;
  navigateToOrganizationChannel: (id: string, scope: ChannelScope) => void;
  organizationId: string;
  organizationIdForRequests?: string;
};

const DefaultChannels = ({
  emptyDescription,
  getPathToEntityDetail,
  navigateToOrganizationChannel,
  organizationId,
  organizationIdForRequests,
}: P) => {
  const [isEmpty, setIsEmpty] = useState<{
    communitiesEmpty: boolean;
    eventsEmpty: boolean;
    givingsEmpty: boolean;
    programsEmpty: boolean;
    videosEmpty: boolean;
  }>({
    communitiesEmpty: false,
    eventsEmpty: false,
    givingsEmpty: false,
    programsEmpty: false,
    videosEmpty: false,
  });

  // TODO this hack is needed together with useEffect in slider components to set empty state correctly
  const [isEmptyState, setIsEmptyState] = useState(false);

  useEffect(() => {
    setIsEmptyState(Object.values(isEmpty).every(Boolean));
  }, [isEmpty]);

  const setCommunitiesEmpty = useCallback((isCommunitiesEmpty: boolean) => {
    setIsEmpty((prevstate) => ({ ...prevstate, communitiesEmpty: isCommunitiesEmpty }));
  }, []);

  const setProgramsEmpty = useCallback((isProgramsEmpty: boolean) => {
    setIsEmpty((prevstate) => ({ ...prevstate, programsEmpty: isProgramsEmpty }));
  }, []);

  const setEventsEmpty = useCallback((isEventsEmpty: boolean) => {
    setIsEmpty((prevstate) => ({ ...prevstate, eventsEmpty: isEventsEmpty }));
  }, []);

  const setGivingsEmpty = useCallback((isGivingsEmpty: boolean) => {
    setIsEmpty((prevstate) => ({ ...prevstate, givingsEmpty: isGivingsEmpty }));
  }, []);

  const setVideosEmpty = useCallback((isVideosEmpty: boolean) => {
    setIsEmpty((state) => ({ ...state, videosEmpty: isVideosEmpty }));
  }, []);

  return (
    <>
      {isEmptyState ? (
        <Card>
          <Row justify="center">
            <Col>
              <Empty description={emptyDescription} image={<Icon color="secondary" icon="compass-line" />} maxWidth />
            </Col>
          </Row>
        </Card>
      ) : null}
      <CommunitiesSlider
        controls={{
          seeAll: {
            limit: 0,
            onClick() {
              navigateToOrganizationChannel(organizationId, 'communities');
            },
          },
          title: 'Communities',
        }}
        getPathToCommunityDetail={(id) => getPathToEntityDetail(id, { entityType: 'community' })}
        onEmptyState={setCommunitiesEmpty}
        query={{ filter: { organizationId: organizationIdForRequests, scope: 'channels' } }}
      />
      <EventsSlider
        controls={{
          seeAll: {
            limit: 0,
            onClick() {
              navigateToOrganizationChannel(organizationId, 'events');
            },
          },
          title: 'Events',
        }}
        getPathToEventDetail={(id) => getPathToEntityDetail(id, { entityType: 'event' })}
        onEmptyState={setEventsEmpty}
        query={{ filter: { organizationId: organizationIdForRequests, scope: 'channels' } }}
      />
      <ProgramsSlider
        controls={{
          seeAll: {
            limit: 0,
            onClick() {
              navigateToOrganizationChannel(organizationId, 'programs');
            },
          },
          title: 'Programs',
        }}
        getPathToProgramDetail={(id) => getPathToEntityDetail(id, { entityType: 'program' })}
        onEmptyState={setProgramsEmpty}
        query={{ filter: { organizationId: organizationIdForRequests, scope: 'channels' } }}
      />
      <GivingsSlider
        controls={{
          seeAll: {
            limit: 0,
            onClick() {
              navigateToOrganizationChannel(organizationId, 'givings');
            },
          },
          title: 'Giving',
        }}
        getPathToGivingDetail={(id) => getPathToEntityDetail(id, { entityType: 'giving' })}
        onEmptyState={setGivingsEmpty}
        query={{ filter: { organizationId: organizationIdForRequests, scope: 'channels' } }}
      />
      <VideosSlider
        controls={{
          seeAll: {
            limit: 0,
            onClick() {
              navigateToOrganizationChannel(organizationId, 'videos');
            },
          },
          title: 'Videos',
        }}
        getPathToVideoDetail={(id) => getPathToEntityDetail(id, { entityType: 'video' })}
        onEmptyState={setVideosEmpty}
        query={{ filter: { organizationId: organizationIdForRequests, scope: 'channels' } }}
      />
    </>
  );
};

export default DefaultChannels;
